import { useEffect, useState } from "react";
import { loadSession } from "./session-storage";
import { SESSION_DURATION_MS, type SessionState } from "./types";

/**
 * Derived re-entry status for the last saved Zenyra conversation.
 *
 * Centralizes the "resume vs. view recommendation" decision that the widget,
 * the CTA cards and the chat surfaces all need. A conversation is `active`
 * (resumable) only while it is unfinished AND still inside the 20-minute
 * window; otherwise it is `ended` and the user is sent to the recommendation
 * screen (which generates the recommendation from the locally-stored messages
 * if it wasn't produced yet).
 */
export type ZenyraStatus =
  | { kind: "none" }
  | { kind: "active"; session: SessionState }
  | { kind: "ended"; session: SessionState; hasRecommendation: boolean };

/** True once the wall-clock 20-minute window has elapsed (pauses excluded). */
export function isExpired(session: SessionState, now = Date.now()): boolean {
  const pausedAcc =
    (session.pausedTotalMs ?? 0) +
    (session.pausedAt ? now - session.pausedAt : 0);
  return session.startedAt + SESSION_DURATION_MS + pausedAcc < now;
}

export function hasRecommendation(session: SessionState): boolean {
  return (
    !!session.match?.topMatch ||
    session.messages.some((m) => m.kind === "recommendation")
  );
}

/** Compute the re-entry status from the single persisted conversation. */
export function getSessionStatus(now = Date.now()): ZenyraStatus {
  const session = loadSession();
  if (!session) return { kind: "none" };

  // Ended explicitly, or the timer ran out while the user was away (the chat
  // tick only runs when /zenyra/chat is open, so we re-check expiry here).
  if (session.endedReason || isExpired(session, now)) {
    return { kind: "ended", session, hasRecommendation: hasRecommendation(session) };
  }

  return { kind: "active", session };
}

/**
 * Client hook: returns the re-entry status once mounted (null during SSR /
 * first paint so server and client markup match).
 */
export function useZenyraStatus(): ZenyraStatus | null {
  const [status, setStatus] = useState<ZenyraStatus | null>(null);
  useEffect(() => {
    setStatus(getSessionStatus());
  }, []);
  return status;
}

export type ReentryTarget = {
  href: string;
  /** Collapsed-button / inline label. */
  buttonLabel: string;
  /** Peek-pill headline. */
  peekTitle: string;
  /** Short CTA verb. */
  cta: string;
  kind: "active" | "ended";
};

/** Where a saved conversation should send the user back to, with copy. */
export function reentryTarget(status: ZenyraStatus | null): ReentryTarget | null {
  if (!status) return null;
  if (status.kind === "active") {
    return {
      href: "/zenyra/chat",
      buttonLabel: "Reia conversația cu Zenyra",
      peekTitle: "Ai o conversație neterminată cu Zenyra",
      cta: "Reia conversația",
      kind: "active",
    };
  }
  if (status.kind === "ended") {
    return {
      href: "/zenyra/final",
      buttonLabel: "Vezi recomandarea ta",
      peekTitle: "Ai vorbit deja cu Zenyra",
      cta: "Vezi recomandarea",
      kind: "ended",
    };
  }
  return null;
}
